import React from 'react';
import { X, ShoppingCart, Plus, Minus, Trash2, AlertCircle } from 'lucide-react';
import { CartItem } from '../utils/pizzaMergeUtils';
import { Store } from '../services/orderLinkAPI';

export type { CartItem };

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  cartItems: CartItem[];
  onUpdateQuantity: (productId: string, attributes: Record<string, any> | undefined, quantity: number) => void;
  onRemoveItem: (productId: string, attributes?: Record<string, any>) => void;
  onRemoveMergedItem: (mergedItem: CartItem) => void;
  onCheckout: () => void;
  store?: Store;
}

const getItemPrice = (item: CartItem): number =>
  item.finalPrice || item.product.price;

const CartDrawer: React.FC<CartDrawerProps> = ({
  isOpen,
  onClose,
  cartItems,
  onUpdateQuantity,
  onRemoveItem,
  onRemoveMergedItem,
  onCheckout,
  store,
}) => {
  const getItemKey = (item: CartItem): string =>
    JSON.stringify({ productId: item.product.id, attributes: item.product_variables || {} });

  const calculateSubtotal = (): number =>
    cartItems.reduce((total, item) => total + getItemPrice(item) * item.quantity, 0);

  const renderAttributes = (item: CartItem) => {
    if (!item.product_variables || Object.keys(item.product_variables).length === 0) {
      return null;
    }

    const attributesList = Object.entries(item.product_variables)
      .filter(([key, value]) => !key.startsWith('_') && value && value !== '')
      .map(([key, value]) =>
        Array.isArray(value) ? `${key}: ${value.join(', ')}` : `${key}: ${value}`
      );

    if (attributesList.length === 0) {
      return null;
    }

    return (
      <div className="text-xs text-gray-500 mt-1">
        {attributesList.join(' • ')}
      </div>
    );
  };

  const renderMergedAttributes = (item: CartItem) => {
    const firstPart = item.items?.[0];
    const vars = firstPart?.product_variables;
    if (!vars) return null;

    const sharedKeys = ['Borda', 'Tamanho'];
    const attributesList = sharedKeys
      .filter(key => vars[key] && vars[key] !== '')
      .map(key => `${key}: ${vars[key]}`);

    if (attributesList.length === 0) return null;

    return (
      <div className="text-xs text-gray-500 mt-1">
        {attributesList.join(' • ')}
      </div>
    );
  };

  const subtotal = calculateSubtotal();

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={onClose}
      />

      <div className="fixed inset-y-0 right-0 w-full sm:w-96 bg-white shadow-xl z-50 transform transition-transform flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" style={{ stroke: 'var(--text-color)' }} />
            <h2 className="text-[22px] font-extrabold tracking-tight text-gray-900">
              Seu Carrinho
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-[4px] transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {cartItems.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg">Seu carrinho está vazio</p>
              <p className="text-gray-400 text-sm mt-2">
                Adicione produtos para continuar
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {cartItems.map((item) => (
                <div
                  key={getItemKey(item)}
                  className="bg-gray-50 rounded-[4px] p-4 border border-[rgba(0,0,0,0.1)]"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h3 className="font-bold text-[15px] text-gray-900 mb-1">
                        {item.displayName || item.product.name}
                      </h3>
                      {item.isMerged
                        ? renderMergedAttributes(item)
                        : renderAttributes(item)
                      }
                      <p className="text-[18px] font-bold mt-1" style={{ color: 'var(--text-color)' }}>
                        R$ {getItemPrice(item).toFixed(2).replace('.', ',')}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        if (item.isMerged) {
                          onRemoveMergedItem(item);
                        } else {
                          onRemoveItem(item.product.id, item.product_variables);
                        }
                      }}
                      className="p-1 hover:bg-error-100 rounded-[4px] transition-colors"
                      title="Remover item"
                    >
                      <Trash2 className="w-4 h-4 text-error-600" />
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    {!item.isMerged ? (
                      <div className="flex items-center border-[1.5px] border-gray-900 rounded-md">
                        <button
                          onClick={() => onUpdateQuantity(item.product.id, item.product_variables, item.quantity - 1)}
                          className="p-2 hover:bg-gray-100 transition-colors"
                          disabled={item.quantity <= 1}
                        >
                          <Minus className="w-4 h-4 text-gray-600" />
                        </button>
                        <span className="px-4 py-2 font-semibold text-gray-900 min-w-[40px] text-center text-[15px]">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => onUpdateQuantity(item.product.id, item.product_variables, item.quantity + 1)}
                          className="p-2 hover:bg-gray-100 transition-colors"
                        >
                          <Plus className="w-4 h-4 text-gray-600" />
                        </button>
                      </div>
                    ) : (
                      <div className="px-4 py-2 text-[14px] text-gray-500">
                        Quantidade fixa: {item.quantity}
                      </div>
                    )}

                    <div className="text-right">
                      <p className="text-[14px] text-gray-500">Subtotal</p>
                      <p className="text-[18px] font-bold text-gray-900">
                        R$ {(getItemPrice(item) * item.quantity).toFixed(2).replace('.', ',')}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {cartItems.length > 0 && (
          <div className="border-t border-gray-200 p-4 bg-gray-50">
            <div className="flex justify-between items-center mb-4">
              <span className="text-[18px] font-bold text-gray-900">
                Subtotal
              </span>
              <span className="text-[22px] font-bold" style={{ color: 'var(--text-color)' }}>
                R$ {subtotal.toFixed(2).replace('.', ',')}
              </span>
            </div>

            {store?.pedido_minimo && subtotal < store.pedido_minimo && (
              <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-[4px] flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                <div className="text-[13px] text-orange-800">
                  <p className="font-semibold mb-1">Pedido mínimo não atingido</p>
                  <p>
                    Adicione mais R$ {(store.pedido_minimo - subtotal).toFixed(2).replace('.', ',')} para atingir o pedido mínimo de R$ {store.pedido_minimo.toFixed(2).replace('.', ',')}
                  </p>
                </div>
              </div>
            )}

            <button
              onClick={onCheckout}
              disabled={store?.pedido_minimo ? subtotal < store.pedido_minimo : false}
              className="w-full btn btn-order-link text-[15px] py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Finalizar Pedido
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;
